import { Fragment } from 'react';
import { Box } from '@mui/material';
import type { ItemMode } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';

import type { UserStandardTreeNode } from '../../../utils/userStandardsTree';

import { LibraryTreeItem } from './LibraryTreeItem';

interface LibraryTreeDndProps {
  roots: UserStandardTreeNode[];
  expanded: Set<string>;
  selectedId: string | null;
  /** Off on touch/narrow layouts — see `LibraryTreeItem`. */
  dragEnabled: boolean;
  onToggleExpand: (id: string) => void;
  onSelect: (node: UserStandardTreeNode) => void;
}

/**
 * The My Standards tree as a flat, Pragmatic-driven render: every visible row is its own
 * draggable + drop target (no per-level dnd-kit context). The drop → move translation, cycle guard,
 * auto-scroll, and post-move flash live in `LibraryDndProvider`, which must wrap this tree.
 */
export function LibraryTreeDnd({
  roots,
  expanded,
  selectedId,
  dragEnabled,
  onToggleExpand,
  onSelect,
}: LibraryTreeDndProps) {
  const renderLevel = (nodes: UserStandardTreeNode[], level: number) =>
    nodes.map((node, index) => {
      // Any node can have children now that partial branches can be imported (an imported standard
      // keeps its child standards), so expandability depends on children, not node_type.
      const isExpandedWithChildren = expanded.has(node.id) && node.children.length > 0;
      const isLast = index === nodes.length - 1;
      // 'expanded' wins: an expanded node's "below" zone belongs to its first child, not a sibling
      // reorder. 'last-in-group' (a leaf/collapsed last sibling) unlocks the outdent (reparent) zone.
      const mode: ItemMode = isExpandedWithChildren
        ? 'expanded'
        : isLast
          ? 'last-in-group'
          : 'standard';

      return (
        <Fragment key={node.id}>
          <LibraryTreeItem
            node={node}
            level={level}
            mode={mode}
            expanded={expanded.has(node.id)}
            selected={selectedId === node.id}
            dragEnabled={dragEnabled}
            onToggleExpand={onToggleExpand}
            onSelect={onSelect}
          />
          {isExpandedWithChildren && renderLevel(node.children, level + 1)}
        </Fragment>
      );
    });

  return <Box sx={{ p: 1 }}>{renderLevel(roots, 0)}</Box>;
}

export default LibraryTreeDnd;
